import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to detect double-shift keypress pattern (Shift+Shift within 500ms)
 * Used to trigger command palette / quick search
 *
 * @param onDoubleShift - Callback to execute when double-shift is detected
 * @param enabled - Whether the hook is active (default: true)
 */
export function useDoubleShift(onDoubleShift: () => void, enabled = true) {
  const lastShiftUpRef = useRef<number>(0);
  const timeoutRef = useRef<number | undefined>(undefined);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    // Only respond to Shift key
    if (e.key !== 'Shift') return;

    // Ignore if user is typing in an input field, textarea, or contenteditable
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }

    const now = Date.now();
    const timeSinceLastShift = now - lastShiftUpRef.current;

    // If two Shift keyups within 500ms, trigger callback
    if (timeSinceLastShift < 500 && timeSinceLastShift > 50) {
      // Reset the timer to prevent triple-tap triggering twice
      lastShiftUpRef.current = 0;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      onDoubleShift();
    } else {
      lastShiftUpRef.current = now;
      // Clear the timestamp after 500ms to prevent stale detection
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        lastShiftUpRef.current = 0;
      }, 500);
    }
  }, [onDoubleShift]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keyup', handleKeyUp);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, handleKeyUp]);
}
